/**
 * `<RecipientBranch>` — visualizes one recipient of a shared deal as a
 * vertical branch hanging off the parent conversation in the version graph.
 *
 * Anchors on the recipient (per-recipient mental model from
 * `external-sharing` redesign 2026-05-10): the parent conversation is the
 * trunk; each `childShare` becomes one branch showing where the shared
 * snapshot went and what the recipient did with it.
 *
 * States rendered:
 * - `pending` — recipient hasn't accepted/forked yet (no fork node exists).
 *   Shows the share row only, no fork card. Compare button disabled.
 * - `active` with fork — recipient signed up (or was already an internal
 *   user) and forked the snapshot into their own conversation. Shows the
 *   fork card + Compare button.
 * - `active` without fork — recipient has access but hasn't forked.
 *   Compare button shown but compares against the share's frozen snapshot
 *   directly (recipient hasn't drifted from it yet).
 * - `revoked` — owner pulled access. Branch dimmed; Compare button disabled.
 */
import clsx from 'clsx';
import type {
  VersionGraphChildShare,
  VersionGraphNode as VersionGraphNodeType,
} from '@arcanum-hq/shared-types';
import { formatRelativeTime } from '@/lib/format';

export interface RecipientBranchProps {
  /** The childShare row from the parent's `childShares` array. */
  share: VersionGraphChildShare;
  /** The fork conversation node for this share, if the recipient has forked. */
  forkNode: VersionGraphNodeType | null;
  /** True when the active conversation is this branch's fork. */
  isCurrent: boolean;
  /**
   * Fired when the user clicks the Compare button. Carries the share id +
   * (optional) fork conversation id so the parent can resolve the diff
   * sides correctly.
   */
  onCompare: (pair: {
    shareId: string;
    forkConversationId: string | null;
    parentConversationId: string;
  }) => void;
  /** The parent conversation id — needed for the diff routing. */
  parentConversationId: string;
}

export function RecipientBranch({
  share,
  forkNode,
  isCurrent,
  onCompare,
  parentConversationId,
}: RecipientBranchProps) {
  const isRevoked = share.status === 'revoked';
  const isPending = share.status === 'pending';
  const compareDisabled = isRevoked;

  return (
    <div
      data-testid="recipient-branch"
      data-share-id={share.shareId}
      data-share-status={share.status}
      className={clsx(
        'flex flex-col gap-2 pl-4 border-l-2 ml-3 mt-3',
        isRevoked
          ? 'border-l-text-dim opacity-50'
          : isPending
            ? 'border-l-warn/40'
            : 'border-l-accent/40',
      )}
    >
      {/* Recipient header — the share row itself */}
      <div className="flex items-start gap-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-mono text-sm text-textBright">
              {share.recipientName}
            </span>
            <span
              title={
                isRevoked
                  ? 'You revoked this share. The link returns 404 to all viewers.'
                  : isPending
                    ? 'Link is live for anyone with the URL. Recipient hasn’t signed up yet.'
                    : 'Recipient has signed in. They can fork or share back.'
              }
              className={clsx(
                'font-mono text-micro uppercase tracking-[0.5px] px-1.5 py-0.5 border rounded-[2px]',
                isRevoked && 'text-text-dim border-text-dim/30 bg-panelBg',
                isPending && 'text-warn border-warn/30 bg-warn/10',
                share.status === 'active' && 'text-accent border-accent/30 bg-accent/10',
              )}
            >
              {isPending ? 'awaiting signup' : share.status}
            </span>
          </div>
          <span className="font-mono text-micro text-text-muted">
            shared {formatRelativeTime(share.createdAt)}
          </span>
        </div>
        <button
          type="button"
          onClick={() =>
            onCompare({
              shareId: share.shareId,
              forkConversationId: forkNode?.conversationId ?? null,
              parentConversationId,
            })
          }
          disabled={compareDisabled}
          data-testid="recipient-branch-compare"
          className={clsx(
            'font-mono text-micro uppercase tracking-[0.5px] px-2 py-1 border rounded-[2px] transition-colors',
            compareDisabled
              ? 'text-text-dim border-text-dim/30 cursor-not-allowed'
              : 'text-accent border-accent/30 bg-accent/10 hover:bg-accent/20 cursor-pointer',
          )}
        >
          {forkNode ? 'Compare versions' : 'Compare to snapshot'}
        </button>
      </div>

      {/* Fork sub-card (when recipient has forked) */}
      {forkNode && (
        <div
          data-testid="recipient-branch-fork"
          className={clsx(
            'flex flex-col gap-1 ml-4 pl-3 border-l border-l-panelBorder py-2',
            isCurrent && 'border-l-accent',
          )}
        >
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-mono text-micro text-text-muted uppercase tracking-[0.5px]">
              {forkNode.ownerName}
            </span>
            {isCurrent && (
              <span className="font-mono text-micro text-accent uppercase tracking-[0.5px]">
                You are here
              </span>
            )}
          </div>
          <span className="font-body text-xs text-text">
            forked {formatRelativeTime(forkNode.createdAt)}
          </span>
          {forkNode.childShares.length > 0 && (
            <span className="font-mono text-micro text-text-muted mt-1">
              {forkNode.childShares.length} share{forkNode.childShares.length === 1 ? '' : 's'} back
            </span>
          )}
        </div>
      )}
      {/* No-fork state hint. Pending recipients already get the
          "awaiting signup" pill above; the hint here covers the active-but-
          unforked case ("they have access, haven't forked"). */}
      {!forkNode && !isRevoked && !isPending && (
        <div className="ml-4 pl-3 border-l border-l-panelBorder py-2">
          <span className="font-mono text-micro text-text-muted italic">
            has access — no fork yet
          </span>
        </div>
      )}
    </div>
  );
}
